import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import EditorIcon from '../../Icons/Nav/editor.svg?react';
import PathBuilderIcon from '../../Icons/Nav/path-builder.svg?react';
import { AppBar, Box, Button, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import type { CssVarsTheme } from '@mui/material/styles';
import { PaletteMode, useColorScheme } from '@mui/material/styles';
import { memo, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEditorTabClick } from '../../../analytics/bpsEvents';
import { features } from '../../../config/features';
import { usePrimaryBarCompactLayout } from '../../../hooks/ui/usePrimaryBarCompactLayout';
import { BPS_PRIVACY_POLICY_PATH } from '../../../Pages/BpsPrivacyPolicyPage';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { LAYOUT_CONSTANTS } from '../../../utils/layout-constants';
import PathBuilderComingSoonDialog from '../../Dialogs/PathBuilder/PathBuilderComingSoonDialog';
import BpsLogo from '../../Icons/BpsLogo';
import ConnectButton from '../BottomBar/ConnectButton';
import ControllerConnectionIcon from './ControllerConnectionIcon';
import ProgramStatusBar from './ProgramStatusBar';
import ProjectTitleBar from './ProjectTitleBar';
import SettingsButton from './SettingsButton';

// Static popper props to prevent Tooltip re-renders
const staticPopperProps = {
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: 'clippingParents',
      },
    },
  ],
};

// Memoized navigation button component to prevent unnecessary re-renders
const NavigationIconButton = memo<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isActive: boolean;
  tooltip: string;
  ariaLabel: string;
  isCompactLayout?: boolean;
}>(({ icon, label, onClick, isActive, tooltip, ariaLabel, isCompactLayout }) => {
  if (isCompactLayout) {
    // Icon-only when the toolbar row is too narrow for labels
    return (
      <Tooltip title={tooltip} disableInteractive enterDelay={500} leaveDelay={0} slotProps={{ popper: staticPopperProps }}>
        <IconButton
          onClick={onClick}
          aria-label={ariaLabel}
          sx={(theme) => ({
            color: isActive ? theme.vars?.palette?.primary?.main : theme.vars?.palette?.text?.primary,
            bgcolor: isActive ? theme.vars?.palette?.action?.selected : 'transparent',
          })}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  }

  // Full button view for larger screens
  return (
    <Tooltip
      title={tooltip}
      disableInteractive
      enterDelay={500}
      leaveDelay={0}
      slotProps={{
        popper: staticPopperProps,
      }}
    >
      <Button
        onClick={onClick}
        variant="nav"
        sx={(theme) => ({
          bgcolor: isActive ? theme.vars?.palette?.action?.selected : 'transparent',
        })}
        aria-label={ariaLabel}
        startIcon={icon}
      >
        <Typography variant="navText">{label}</Typography>
      </Button>
    </Tooltip>
  );
});

NavigationIconButton.displayName = 'NavigationIconButton';

const PrimaryBar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Use granular selectors to prevent unnecessary re-renders
  const projectId = useAppSelector((state) => state.project.loadedProject?.id);
  const accessLevel = useAppSelector((state) => state.project.loadedProject?.accessLevel);
  const projectReadOnly = useAppSelector((state) => state.project.loadedProject?.readOnly ?? false);
  const projectName = useAppSelector((state) => state.project.loadedProject?.name ?? '');

  const { t } = useTranslation();

  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const handleCloseComingSoon = useCallback(() => setComingSoonOpen(false), []);

  const { mode, systemMode } = useColorScheme();
  let colorMode: PaletteMode | undefined;
  if (mode === 'system') {
    colorMode = systemMode || 'light';
  } else {
    colorMode = mode;
  }

  // Memoize pathname checks
  const isPrivacyPolicyPage = useMemo(() => pathname.startsWith(BPS_PRIVACY_POLICY_PATH), [pathname]);
  const isHomePage = useMemo(() => pathname === '/' || pathname.startsWith('/examples') || pathname.startsWith('/rapid-robots'), [pathname]);
  const isProjectPage = useMemo(() => !isHomePage, [isHomePage]);
  const isEditorPage = useMemo(() => pathname.includes('/editor'), [pathname]);
  const isDashboardPage = useMemo(() => pathname.includes('/dashboard'), [pathname]);
  const isPathBuilderPage = useMemo(() => pathname.includes('/pathbuilder'), [pathname]);
  const isProjectViewPage = useMemo(() => isEditorPage || isDashboardPage || isPathBuilderPage, [isEditorPage, isDashboardPage, isPathBuilderPage]);

  const handleBack = () => {
    navigate('/');
  };

  const handleNavigateToEditor = useCallback(() => {
    if (projectId) {
      trackEditorTabClick('program');
      navigate(`/editor/${projectId}`);
    }
  }, [navigate, projectId]);

  const handleNavigateToDashboard = useCallback(() => {
    if (projectId) {
      trackEditorTabClick('dashboard');
      navigate(`/dashboard/${projectId}`);
    }
  }, [navigate, projectId]);

  // Memoize tooltip strings to prevent re-renders
  const tooltipStrings = useMemo(
    () => ({
      program: t('primaryBar.tooltip.program', 'Program'),
      dashboard: t('primaryBar.tooltip.dashboard', 'Dashboard'),
      pathBuilder: t('primaryBar.tooltip.pathBuilder', 'Path Builder'),
      home: t('primaryBar.tooltip.home', 'Back to Projects'),
    }),
    [t]
  );

  const ariaLabels = useMemo(
    () => ({
      program: t('aria-label.program', 'program'),
      dashboard: t('aria-label.dashboard', 'dashboard'),
      pathBuilder: t('aria-label.pathBuilder', 'path builder'),
      home: t('aria-label.home', 'home'),
    }),
    [t]
  );

  const handleNavigateToPathBuilder = useCallback(() => {
    if (!projectId) {
      return;
    }
    trackEditorTabClick('path_builder');
    // When Path Builder is gated (any stage other than staging/rc, e.g. production),
    // show the "Coming Soon" dialog in place instead of navigating, so the current
    // page is not unmounted and reloaded.
    if (!features.pathBuilder) {
      setComingSoonOpen(true);
      return;
    }
    navigate(`/pathbuilder/${projectId}`);
  }, [navigate, projectId]);

  const showProjectTitle = Boolean(isProjectViewPage && projectId);
  const showViewerReadOnlyBanner = accessLevel === 'viewer' && isProjectViewPage;
  const rightLayoutSignature = `${accessLevel ?? 'local'}:${projectReadOnly}`;
  const { rowRef, leftSectionRef, rightSectionRef, titleMeasureRef, readOnlyBannerMeasureRef, isCompactLayout } = usePrimaryBarCompactLayout({
    enabled: showProjectTitle,
    showProjectTitle,
    showTitleEditButton: showProjectTitle && !projectReadOnly,
    showViewerReadOnlyBanner,
    remeasureKey: projectName,
    rightLayoutSignature,
  });

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 3, overflow: 'visible' }}>
      <Toolbar>
        <Stack ref={rowRef} direction="row" alignItems="center" width="100%" useFlexGap sx={{ minWidth: 0, flexWrap: 'nowrap' }}>
          <Stack
            ref={leftSectionRef}
            alignItems="center"
            spacing={2}
            direction="row"
            sx={{ flexShrink: 0, marginLeft: isProjectPage ? '-24px' : '0px' }}
          >
            {isHomePage && (
              <BpsLogo
                colormode={colorMode}
                height={40}
                role="img"
                aria-label={t('aria.logo', 'Sphero BPS')}
                style={{ display: 'block', transform: 'translateX(-30px) scale(1.6)', transformOrigin: 'left center' }}
              />
            )}

            {isProjectPage && (
              <Tooltip title={tooltipStrings.home} disableInteractive enterDelay={500} leaveDelay={0} slotProps={{ popper: staticPopperProps }}>
                <Button
                  onClick={handleBack}
                  sx={(theme) => {
                    const t = theme as unknown as CssVarsTheme;
                    return {
                      color: 'inherit',
                      borderRadius: '0%',
                      minWidth: '94px',
                      width: '94px',
                      height: 'auto',
                      padding: '8px 0',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.3,
                      textTransform: 'uppercase',
                      '&:hover': {
                        backgroundColor: t.vars?.palette?.action?.hover,
                      },
                    };
                  }}
                  aria-label={ariaLabels.home}
                >
                  <HomeIcon />
                </Button>
              </Tooltip>
            )}

            {/* Navigation icons - show when project is loaded, aligned with drawer icon */}
            {projectId && isProjectViewPage && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  marginLeft: `${LAYOUT_CONSTANTS.PAGE_NAV_TAB_SPACING}px !important`,
                }}
              >
                <NavigationIconButton
                  icon={<EditorIcon style={{ width: 24, height: 24 }} />}
                  label={tooltipStrings.program}
                  onClick={handleNavigateToEditor}
                  isActive={isEditorPage}
                  tooltip={tooltipStrings.program}
                  ariaLabel={ariaLabels.program}
                  isCompactLayout={isCompactLayout}
                />
                <NavigationIconButton
                  icon={<DashboardIcon sx={{ fontSize: 24 }} />}
                  label={tooltipStrings.dashboard}
                  onClick={handleNavigateToDashboard}
                  isActive={isDashboardPage}
                  tooltip={tooltipStrings.dashboard}
                  ariaLabel={ariaLabels.dashboard}
                  isCompactLayout={isCompactLayout}
                />
                <NavigationIconButton
                  icon={<PathBuilderIcon style={{ width: 24, height: 24 }} />}
                  label={tooltipStrings.pathBuilder}
                  onClick={handleNavigateToPathBuilder}
                  isActive={isPathBuilderPage}
                  tooltip={tooltipStrings.pathBuilder}
                  ariaLabel={ariaLabels.pathBuilder}
                  isCompactLayout={isCompactLayout}
                />
              </Stack>
            )}
          </Stack>

          {showProjectTitle ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center',
                px: 1,
              }}
            >
              <ProjectTitleBar measureRef={titleMeasureRef} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }} />
          )}

          <Stack ref={rightSectionRef} spacing={2} direction="row" alignItems="center" useFlexGap sx={{ flexShrink: 0 }}>
            {accessLevel === 'viewer' &&
              isProjectViewPage &&
              (isCompactLayout ? (
                <Tooltip
                  title={t('primaryBar.text.read-only-mode', 'You are in read-only mode. See control options on the bottom bar.')}
                  slotProps={{ popper: staticPopperProps }}
                >
                  <IconButton
                    size="small"
                    color="info"
                    sx={{
                      border: 1,
                      borderColor: 'info.main',
                    }}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Typography noWrap sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Trans i18nKey={'primaryBar.text.read-only-mode'}>You are in read-only mode. See control options on the bottom bar.</Trans>
                </Typography>
              ))}
            {!isPrivacyPolicyPage && <ConnectButton />}
            <ControllerConnectionIcon />
            <SettingsButton />
          </Stack>
        </Stack>
      </Toolbar>
      {showViewerReadOnlyBanner ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            height: 0,
            overflow: 'hidden',
          }}
        >
          <Typography
            ref={readOnlyBannerMeasureRef}
            data-testid="primary-bar-read-only-banner-measure"
            noWrap
            sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            <Trans i18nKey={'primaryBar.text.read-only-mode'}>You are in read-only mode. See control options on the bottom bar.</Trans>
          </Typography>
        </Box>
      ) : null}
      <ProgramStatusBar />
      <PathBuilderComingSoonDialog open={comingSoonOpen} onClose={handleCloseComingSoon} />
    </AppBar>
  );
};

export default PrimaryBar;
